import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChartBar, TrendUp, FolderSimple, ListChecks, FileText, ChatCircle, Receipt, FilePdf, Handshake, CheckCircle, Clock, X, Star, FloppyDisk, Trash, CaretDown, Link as LinkIcon } from "@phosphor-icons/react";
import QuoteFunnelSegmentDialog from "@/components/app/QuoteFunnelSegmentDialog";
import OnboardingAnalyticsTile from "@/components/app/OnboardingAnalyticsTile";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const FUNNEL_PRESETS_KEY = "analytics.funnelPresets.v1";
const FUNNEL_DEFAULTS = { funnelDays: 0, funnelChannel: "all", funnelTxType: "all", funnelPropType: "all" };

const COLORS_STATUS = ["#2563eb", "#16a34a"];
const COLORS_TYPE = ["#2563eb", "#f59e0b"];
const COLORS_STAGE = ["#2563eb", "#7c3aed", "#16a34a", "#f59e0b", "#ef4444", "#06b6d4"];

const formatGBP = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n || 0);

export default function AnalyticsPage() {
  const navigate = useNavigate();

  // Read filter state from the URL on first render so deep-link share
  // URLs (e.g. ?days=30&channel=referrer&txt=buying) restore the exact
  // view the sender was looking at. Falls back to defaults for any
  // missing/invalid params.
  const _initialFromUrl = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const _days = parseInt(p.get("days") || "", 10);
      const _ch = p.get("channel");
      const _txt = p.get("txt");
      const _prop = p.get("prop");
      const VALID_CH = ["all", "referrer", "public_solicitor", "public_website"];
      const VALID_TXT = ["all", "buying", "selling", "both", "remortgage", "transfer"];
      const VALID_PROP = ["all", "freehold", "leasehold"];
      return {
        funnelDays:     [7, 30, 90, 0].includes(_days) ? _days : 0,
        funnelChannel:  VALID_CH.includes(_ch)   ? _ch   : "all",
        funnelTxType:   VALID_TXT.includes(_txt) ? _txt  : "all",
        funnelPropType: VALID_PROP.includes(_prop) ? _prop : "all",
      };
    } catch { return { ...FUNNEL_DEFAULTS }; }
  })();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  // Window for the quote-funnel section. 0 = all time.
  const [funnelDays, setFunnelDays] = useState(_initialFromUrl.funnelDays);
  // Channel filter — re-points every funnel/revenue tile to a single
  // acquisition source. "all" = sum across all sources (legacy view).
  const [funnelChannel, setFunnelChannel] = useState(_initialFromUrl.funnelChannel);
  // Transaction-type filter — narrows every funnel + revenue tile to
  // a single matter type (e.g. just remortgages). "all" preserves the
  // cross-type aggregate. Backend-side filter — requires a re-fetch.
  const [funnelTxType, setFunnelTxType] = useState(_initialFromUrl.funnelTxType);
  // Property-type filter — freehold / leasehold. Backend filter.
  const [funnelPropType, setFunnelPropType] = useState(_initialFromUrl.funnelPropType);

  // Saved filter presets — per-browser via localStorage so the user can
  // re-apply a common view ("My Monday morning view") in one click. No
  // backend persistence yet; if presets need to follow the user across
  // devices we'd move this to a user-prefs collection later.
  const [presets, setPresets] = useState(() => {
    try {
      const raw = localStorage.getItem(FUNNEL_PRESETS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const persistPresets = (next) => {
    setPresets(next);
    try { localStorage.setItem(FUNNEL_PRESETS_KEY, JSON.stringify(next)); } catch { /* quota — ignore */ }
  };

  // True when any filter is off-default — drives both the "Reset filters"
  // chip visibility and the disabled-state of the "Save preset" item.
  const anyFilterActive =
    funnelDays !== FUNNEL_DEFAULTS.funnelDays ||
    funnelChannel !== FUNNEL_DEFAULTS.funnelChannel ||
    funnelTxType !== FUNNEL_DEFAULTS.funnelTxType ||
    funnelPropType !== FUNNEL_DEFAULTS.funnelPropType;

  const resetFunnelFilters = () => {
    setFunnelDays(FUNNEL_DEFAULTS.funnelDays);
    setFunnelChannel(FUNNEL_DEFAULTS.funnelChannel);
    setFunnelTxType(FUNNEL_DEFAULTS.funnelTxType);
    setFunnelPropType(FUNNEL_DEFAULTS.funnelPropType);
  };

  const applyPreset = (p) => {
    setFunnelDays(p.funnelDays ?? 0);
    setFunnelChannel(p.funnelChannel ?? "all");
    setFunnelTxType(p.funnelTxType ?? "all");
    setFunnelPropType(p.funnelPropType ?? "all");
  };

  const saveCurrentAsPreset = () => {
    const name = window.prompt("Name this view (e.g. 'Monday referrer leasehold check')");
    if (!name) return;
    const trimmed = name.trim().slice(0, 60);
    if (!trimmed) return;
    if (presets.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("A preset with that name already exists");
      return;
    }
    const next = [...presets, {
      name: trimmed,
      funnelDays, funnelChannel, funnelTxType, funnelPropType,
    }];
    persistPresets(next);
    toast.success(`Saved preset "${trimmed}"`);
  };

  const deletePreset = (name) => {
    persistPresets(presets.filter((p) => p.name !== name));
    toast.success(`Removed preset "${name}"`);
  };

  // Build & copy a deep-link URL that restores the current filter set
  // when opened. Only includes non-default values so the URL stays
  // short for the common-case all-defaults state.
  const copyShareLink = async () => {
    const qs = new URLSearchParams();
    if (funnelDays !== FUNNEL_DEFAULTS.funnelDays) qs.set("days", String(funnelDays));
    if (funnelChannel !== FUNNEL_DEFAULTS.funnelChannel) qs.set("channel", funnelChannel);
    if (funnelTxType !== FUNNEL_DEFAULTS.funnelTxType) qs.set("txt", funnelTxType);
    if (funnelPropType !== FUNNEL_DEFAULTS.funnelPropType) qs.set("prop", funnelPropType);
    const search = qs.toString();
    const url = `${window.location.origin}${window.location.pathname}${search ? "?" + search : ""}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch {
      // Fallback for non-secure contexts where clipboard API is blocked.
      window.prompt("Copy this link:", url);
    }
  };
  // Channel comparison panel — when on, renders a side-by-side breakdown
  // of every channel's funnel + revenue numbers below the main tiles.
  // Defaults off so the page stays compact on first load; one click opens
  // the comparison without forcing a layout shift.
  const [showCompare, setShowCompare] = useState(false);
  // Scope toggle — "mine" (caseload-only) vs "all" (firm-wide).
  // Sticky in localStorage so the user's preference survives reloads.
  const [scope, setScope] = useState(() => localStorage.getItem("analytics-scope") || "mine");
  useEffect(() => { localStorage.setItem("analytics-scope", scope); }, [scope]);
  // Drill-down dialog state — populated when a solicitor clicks a
  // funnel tile so they can see the underlying quotes.
  const [openSegment, setOpenSegment] = useState(null);

  const fetchAnalytics = useCallback(async (days = 0, scopeArg = "mine", txType = "all", propType = "all") => {
    try {
      const { data } = await API.get("/analytics", {
        params: { funnel_days: days, scope: scopeArg, transaction_type: txType, property_type: propType },
      });
      setAnalytics(data);
    } catch (err) {
      console.error("Failed to fetch analytics", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics(funnelDays, scope, funnelTxType, funnelPropType);
  }, [fetchAnalytics, funnelDays, scope, funnelTxType, funnelPropType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Onboarding funnel tile still renders — it's on its own endpoint
              and doesn't depend on the quote-analytics payload. */}
          <OnboardingAnalyticsTile />
          <p className="text-slate-500 text-center py-8">Quote analytics could not be loaded — try refreshing, or contact your admin.</p>
        </div>
      </div>
    );
  }

  const { summary, cases_by_status, cases_by_type, stage_distribution, monthly_completions, quote_funnel, top_referrers } = analytics;

  // Quote conversion funnel — % conversion at each step. Defensive against
  // a missing block (older /analytics responses) so the page still renders.
  const funnel = quote_funnel || { total_quotes: 0, pdfs_downloaded: 0, quotes_with_pdf: 0, pdfs_last_7_days: 0, client_instructed: 0, instructed: 0 };

  // Channel filter — re-points every funnel/revenue tile to a single
  // acquisition channel, or shows the headline totals across all
  // channels. The drill-down dialog stays firm-wide for now (it would
  // need a backend filter param to be channel-aware).
  // `funnelView` exposes the same field names as `funnel` regardless
  // of channel, so the tile config below doesn't need to branch.
  const slice = funnelChannel !== "all" && funnel.by_source_breakdown
    ? funnel.by_source_breakdown[funnelChannel] || {}
    : null;
  const funnelView = slice ? {
    ...funnel, // carry through anything not in the slice (e.g. `by_source` headline labels)
    total_quotes:        slice.total_quotes ?? 0,
    pdfs_downloaded:     slice.pdfs_downloaded ?? 0,
    quotes_with_pdf:     slice.quotes_with_pdf ?? 0,
    pdfs_last_7_days:    slice.pdfs_last_7_days ?? 0,
    not_accepted:        slice.not_accepted ?? 0,
    client_instructed:   slice.accepted ?? 0,
    portal_accepted:     slice.portal_accepted ?? 0,
    direct_instructed:   slice.direct_instructed ?? 0,
    instructed:          slice.instructed ?? 0,
    gross_legal_fees:    slice.gross_legal_fees ?? 0,
    net_legal_fees:      slice.net_legal_fees ?? 0,
    vat_collected:       slice.vat_collected ?? 0,
    referral_fees_paid:  slice.referral_fees_paid ?? 0,
    disbursement_uplift: slice.disbursement_uplift ?? 0,
    by_source:           undefined, // suppress origin-breakdown subline (it's already filtered)
  } : funnel;
  // Compute the % of the most appropriate previous step. If the previous step
  // is somehow smaller than the current (historical cases that were instructed
  // before the client_instructed/PDF tracking existed), fall back to comparing
  // against the total quotes so the number is never >100%.
  const pct = (current, prev) => {
    if (!prev || prev <= 0) return "—";
    const v = (current / prev) * 100;
    if (v > 100) return null; // signal: hide the % rather than showing >100%
    return `${v.toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="analytics-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {scope === "all"
                ? "Firm-wide view — every case across every solicitor"
                : "Your own caseload"}
            </p>
          </div>
          {/* Scope toggle — solicitors can flip between their own
              caseload and the whole firm. Backend already enforces the
              filter; this just changes the ``?scope=`` query param. */}
          <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="analytics-scope-toggle">
            {[
              { v: "mine", label: "My caseload" },
              { v: "all", label: "Firm-wide" },
            ].map((opt) => {
              const on = scope === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setScope(opt.v)}
                  data-testid={`analytics-scope-${opt.v}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    on ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: "Total Cases", value: summary.total_cases, icon: FolderSimple, color: "text-slate-700", bg: "bg-slate-100" },
            { label: "Active", value: summary.active_cases, icon: ChartBar, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Completed", value: summary.completed_cases, icon: TrendUp, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Avg Progress", value: `${summary.avg_progress}%`, icon: ChartBar, color: "text-violet-600", bg: "bg-violet-50" },
            { label: "Tasks (7d)", value: summary.recent_tasks, icon: ListChecks, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Notes (7d)", value: summary.recent_notes, icon: ChatCircle, color: "text-cyan-600", bg: "bg-cyan-50" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`analytics-${stat.label.toLowerCase().replace(/[\s()]/g, "-")}`}>
              <div className={`w-8 h-8 ${stat.bg} rounded-lg flex items-center justify-center mb-2`}>
                <stat.icon weight="fill" className={`w-4 h-4 ${stat.color}`} />
              </div>
              <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
              <p className="text-[11px] text-slate-500 font-medium mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Firm-wide onboarding funnel — magic-link → TA signed, broken
            down by referrer. Read-only tile driven by GET /api/onboarding/
            analytics/summary. Solicitor-only endpoint. */}
        <OnboardingAnalyticsTile />

        {/* Quote conversion funnel — Quoted → PDF Downloaded → Client Accepted → Case Opened.
            Conversion % under each step is calculated against the previous step (drop-off
            view) rather than against total, so each card answers "how many of the previous
            stage made it here". */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm mb-8" data-testid="quote-funnel">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Quote Conversion Funnel</h3>
              <p className="text-xs text-slate-500 mt-0.5">From quote generation to opened case · firm-wide</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Date-range filter — segmented toggle. 0 = all time. */}
              <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="funnel-window-toggle">
                {[
                  { d: 7, label: "7d" },
                  { d: 30, label: "30d" },
                  { d: 90, label: "90d" },
                  { d: 0, label: "All" },
                ].map((opt) => {
                  const on = funnelDays === opt.d;
                  return (
                    <button
                      key={opt.d}
                      type="button"
                      onClick={() => setFunnelDays(opt.d)}
                      data-testid={`funnel-window-${opt.label.toLowerCase()}`}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                        on ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {/* Channel filter — repoints every tile + revenue figure to
                  a single acquisition source. "All" sums everything (the
                  pre-existing behaviour). */}
              <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="funnel-channel-toggle">
                {[
                  { v: "all",              label: "All" },
                  { v: "referrer",         label: "Referrer" },
                  { v: "public_solicitor", label: "Solicitor" },
                  { v: "public_website",   label: "Public" },
                ].map((opt) => {
                  const on = funnelChannel === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setFunnelChannel(opt.v)}
                      data-testid={`funnel-channel-${opt.v}`}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                        on ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {/* Property-type filter — freehold vs leasehold. Useful
                  to confirm e.g. that leaseholds disproportionately
                  drive PII/admin overhead per matter. */}
              <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="funnel-proptype-toggle">
                {[
                  { v: "all",       label: "All tenure" },
                  { v: "freehold",  label: "Freehold" },
                  { v: "leasehold", label: "Leasehold" },
                ].map((opt) => {
                  const on = funnelPropType === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setFunnelPropType(opt.v)}
                      data-testid={`funnel-proptype-${opt.v}`}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap ${
                        on ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {/* Compare-channels toggle — shows every channel's numbers
                  side-by-side below the main funnel. Independent of the
                  channel filter above (which still controls the top tiles). */}
              <button
                type="button"
                onClick={() => setShowCompare(s => !s)}
                data-testid="funnel-compare-toggle"
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors border ${
                  showCompare
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {showCompare ? "Hide comparison" : "Compare channels"}
              </button>

              {/* Saved-presets dropdown — apply / save / delete preset
                  combinations of (days, channel, tx type, tenure). */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="funnel-presets-trigger"
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border bg-white text-slate-600 border-slate-200 hover:border-slate-400 transition-colors"
                  >
                    <Star weight="bold" className="w-3 h-3" />
                    Presets
                    <CaretDown weight="bold" className="w-3 h-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">
                    Saved views
                  </DropdownMenuLabel>
                  {presets.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-slate-400 italic">No saved views yet.</div>
                  )}
                  {presets.map((p) => (
                    <div key={p.name} className="flex items-center gap-1 px-1" data-testid={`funnel-preset-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <DropdownMenuItem
                        onClick={() => applyPreset(p)}
                        className="flex-1 cursor-pointer"
                      >
                        <Star weight="fill" className="w-3 h-3 mr-2 text-amber-500" />
                        <span className="truncate">{p.name}</span>
                      </DropdownMenuItem>
                      <button
                        type="button"
                        onClick={() => deletePreset(p.name)}
                        className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                        title={`Delete "${p.name}"`}
                        data-testid={`funnel-preset-delete-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Trash weight="bold" className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={saveCurrentAsPreset}
                    disabled={!anyFilterActive}
                    className="cursor-pointer"
                    data-testid="funnel-preset-save"
                  >
                    <FloppyDisk weight="bold" className="w-3.5 h-3.5 mr-2" />
                    Save current filters…
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={copyShareLink}
                    className="cursor-pointer"
                    data-testid="funnel-share-link"
                  >
                    <LinkIcon weight="bold" className="w-3.5 h-3.5 mr-2" />
                    Copy share link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Reset-filters chip — only rendered when any filter is
                  non-default. One click returns the funnel to the
                  default firm-wide / all-time view. */}
              {anyFilterActive && (
                <button
                  type="button"
                  onClick={resetFunnelFilters}
                  data-testid="funnel-reset"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
                >
                  <X weight="bold" className="w-3 h-3" />
                  Reset filters
                </button>
              )}
              {/* Transaction-type filter — narrows every tile + revenue
                  figure to one matter type (e.g. only remortgages).
                  Backend filter — triggers a fresh fetch. */}
              <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="funnel-txtype-toggle">
                {[
                  { v: "all",        label: "All types" },
                  { v: "buying",     label: "Purchase" },
                  { v: "selling",    label: "Sale" },
                  { v: "both",       label: "Sale + Purchase" },
                  { v: "remortgage", label: "Remortgage" },
                  { v: "transfer",   label: "Transfer of equity" },
                ].map((opt) => {
                  const on = funnelTxType === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setFunnelTxType(opt.v)}
                      data-testid={`funnel-txtype-${opt.v}`}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap ${
                        on ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {funnelView.pdfs_last_7_days > 0 && funnelDays !== 7 && funnelDays !== 0 ? null : funnelView.pdfs_last_7_days > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700" data-testid="funnel-pdf-7d">
                  <TrendUp weight="bold" className="w-3 h-3" /> {funnelView.pdfs_last_7_days} PDF{funnelView.pdfs_last_7_days === 1 ? "" : "s"} this week
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: "Quoted", value: funnelView.total_quotes,
                // Origin breakdown — only meaningful in the "All" view.
                // Suppress when a channel is selected (it's already filtered).
                sub: funnelChannel === "all" && funnelView.by_source
                  ? `${funnelView.by_source.solicitor || 0} solicitor · ${funnelView.by_source.referrer || 0} referrer · ${funnelView.by_source.public || 0} public`
                  : undefined,
                icon: Receipt, accent: "bg-slate-100 text-slate-700", barFrom: 0, barTo: funnelView.total_quotes, prev: null, segment: "quoted", testid: "funnel-quoted" },
              { label: "PDFs Downloaded", value: funnelView.pdfs_downloaded, sub: `${funnelView.quotes_with_pdf} quote${funnelView.quotes_with_pdf === 1 ? "" : "s"}`, icon: FilePdf, accent: "bg-amber-50 text-amber-700", prev: funnelView.total_quotes, vsLabel: "of quoted", segment: "downloaded", testid: "funnel-pdf" },
              // "Not Accepted" sits between PDFs Downloaded and Client
              // Accepted so the firm can see the chase-able cohort
              // alongside the win column.
              { label: "Not Accepted", value: funnelView.not_accepted, icon: Clock, accent: "bg-rose-50 text-rose-700", prev: funnelView.total_quotes, vsLabel: "of quoted", segment: "not_accepted", testid: "funnel-not-accepted" },
              { label: "Client Accepted", value: funnelView.client_instructed,
                sub: (funnelView.portal_accepted != null || funnelView.direct_instructed != null)
                  ? `${funnelView.portal_accepted || 0} portal · ${funnelView.direct_instructed || 0} direct`
                  : undefined,
                // % of quoted (consistent base across all tiles) — was
                // % of viewed PDFs which was misleading when the PDF
                // download tracking undercounts solicitor reviews.
                icon: Handshake, accent: "bg-blue-50 text-blue-700", prev: funnelView.total_quotes, vsLabel: "of quoted", segment: "accepted", testid: "funnel-accepted" },
              { label: "Case Opened", value: funnelView.instructed, icon: CheckCircle, accent: "bg-emerald-50 text-emerald-700", prev: funnelView.total_quotes, vsLabel: "of quoted", segment: "opened", testid: "funnel-opened" },
            ].map((step, idx) => (
              <button
                key={step.label}
                type="button"
                onClick={() => setOpenSegment({ segment: step.segment, label: step.label })}
                className="relative bg-slate-50 border border-slate-200 rounded-lg p-4 text-left hover:border-slate-400 hover:bg-white transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid={step.testid}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-8 h-8 ${step.accent} rounded-lg flex items-center justify-center`}>
                    <step.icon weight="fill" className="w-4 h-4" />
                  </div>
                  <span className="text-[10px] font-semibold text-slate-400 tracking-wider uppercase">Step {idx + 1}</span>
                </div>
                <p className="text-2xl font-bold text-slate-900 leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.value}</p>
                <p className="text-xs font-medium text-slate-700 mt-1">{step.label}</p>
                <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
                  {step.sub && <span>{step.sub}</span>}
                  {step.prev != null && pct(step.value, step.prev) && (
                    <span className="font-semibold text-slate-700">
                      {pct(step.value, step.prev)}
                      <span className="font-normal text-slate-400"> {step.vsLabel}</span>
                    </span>
                  )}
                </div>
                <span className="absolute top-3 right-3 text-[9px] text-slate-400 group-hover:text-slate-600">View →</span>
              </button>
            ))}
          </div>

          {/* Period-revenue summary — appears below the funnel grid using
              the same date window. ``Net legal fees`` is the figure the
              firm actually earns: gross legal fees minus referrer
              commissions. VAT and disbursements are excluded because
              they're collected on behalf of HMRC / searches / lenders
              rather than firm revenue. */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4" data-testid="funnel-revenue-tiles">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4" data-testid="funnel-net-fees">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">
                Net legal fees, ex VAT ({funnelDays === 0 ? "all time" : `last ${funnelDays}d`})
              </p>
              <p className="text-2xl font-bold text-emerald-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatGBP(funnelView.net_legal_fees || 0)}
              </p>
              <p className="text-[10px] text-emerald-700/80 mt-0.5" title="Gross legal fees (ex VAT) − referrer commissions. Gross already includes the ex-VAT mark-up on ID checks (£16.80 each), Search Pack (£120) and TT fees (£18) reclassified out of disbursements.">
                Gross − referral commissions. Includes ID/search/TT mark-up{funnelView.disbursement_uplift > 0
                  ? ` (+${formatGBP(funnelView.disbursement_uplift)})`
                  : ""}.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="funnel-gross-fees">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                Gross legal fees, ex VAT
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatGBP(funnelView.gross_legal_fees || 0)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5" title="ID-check mark-up over £16.80/check, Search Pack mark-up over £120.00/pack, and TT fee mark-up over £18.00/transfer are reclassified out of disbursements and into legal fee revenue.">
                Includes mark-up on ID checks / searches / TT fees{funnelView.disbursement_uplift > 0
                  ? ` (+${formatGBP(funnelView.disbursement_uplift)})`
                  : ""}.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="funnel-vat-collected">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                VAT collected
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatGBP(funnelView.vat_collected || 0)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">Output tax on the legal fee. Payable to HMRC, not firm revenue.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="funnel-referral-fees">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                Referral commissions
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatGBP(funnelView.referral_fees_paid || 0)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">Total paid to referrers in the period.</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4" data-testid="funnel-instructions-total">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-700">
                Total instructions
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {funnelView.instructed || 0}
              </p>
              <p className="text-[10px] text-blue-700/80 mt-0.5">
                {funnelView.net_legal_fees > 0 && funnelView.instructed > 0
                  ? `Avg ${formatGBP(funnelView.net_legal_fees / funnelView.instructed)} net / matter`
                  : "Instructed quotes in this window."}
              </p>
            </div>
          </div>
        </div>

        {/* Channel comparison — shows all three acquisition channels
            side-by-side on the same date + transaction-type window.
            Surfaces conversion gaps without forcing the user to flip
            the channel toggle back and forth. Toggled via the
            "Compare channels" button next to the channel filter. */}
        {showCompare && funnel.by_source_breakdown && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm mb-8" data-testid="funnel-compare-panel">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Channel comparison
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Side-by-side metrics for the current {funnelDays === 0 ? "all-time" : `${funnelDays}-day`} window
                {funnelTxType !== "all" ? `, filtered to ${({
                  buying: "Purchases", selling: "Sales", both: "Sale + Purchase",
                  remortgage: "Remortgages", transfer: "Transfers of equity",
                }[funnelTxType])}` : ""}.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="funnel-compare-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-2 py-2 font-semibold">Metric</th>
                    <th className="px-2 py-2 font-semibold text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-purple-500" /> Referrer
                      </span>
                    </th>
                    <th className="px-2 py-2 font-semibold text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" /> Public website
                      </span>
                    </th>
                    <th className="px-2 py-2 font-semibold text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-500" /> Solicitor built
                      </span>
                    </th>
                    <th className="px-2 py-2 font-semibold text-right text-slate-700">Total</th>
                  </tr>
                </thead>
                <tbody className="text-slate-800">
                  {(() => {
                    const r = funnel.by_source_breakdown.referrer || {};
                    const w = funnel.by_source_breakdown.public_website || {};
                    const s = funnel.by_source_breakdown.public_solicitor || {};
                    const conv = (slice) => slice.total_quotes ? Math.round((slice.instructed / slice.total_quotes) * 100) : 0;
                    const rows = [
                      { label: "Quotes",          fmt: (v) => v.toLocaleString(),  r: r.total_quotes  ?? 0, w: w.total_quotes  ?? 0, s: s.total_quotes  ?? 0 },
                      { label: "Accepted",        fmt: (v) => v.toLocaleString(),  r: r.accepted      ?? 0, w: w.accepted      ?? 0, s: s.accepted      ?? 0, accent: "text-emerald-700" },
                      { label: "Not accepted",    fmt: (v) => v.toLocaleString(),  r: r.not_accepted  ?? 0, w: w.not_accepted  ?? 0, s: s.not_accepted  ?? 0, accent: "text-rose-700" },
                      { label: "Instructions",    fmt: (v) => v.toLocaleString(),  r: r.instructed    ?? 0, w: w.instructed    ?? 0, s: s.instructed    ?? 0, accent: "text-blue-700 font-semibold" },
                      { label: "Conversion",      fmt: (v) => `${v}%`,             r: conv(r),              w: conv(w),              s: conv(s) },
                      { label: "Gross fees",      fmt: (v) => formatGBP(v),         r: r.gross_legal_fees ?? 0, w: w.gross_legal_fees ?? 0, s: s.gross_legal_fees ?? 0 },
                      { label: "Net fees",        fmt: (v) => formatGBP(v),         r: r.net_legal_fees   ?? 0, w: w.net_legal_fees   ?? 0, s: s.net_legal_fees   ?? 0, accent: "text-emerald-700 font-semibold" },
                      { label: "Avg net / matter", fmt: (v) => v ? formatGBP(v) : "—", r: r.instructed ? (r.net_legal_fees / r.instructed) : 0, w: w.instructed ? (w.net_legal_fees / w.instructed) : 0, s: s.instructed ? (s.net_legal_fees / s.instructed) : 0 },
                    ];
                    return rows.map((row, idx) => {
                      const total = row.label === "Conversion"
                        ? (() => {
                            const tQ = (r.total_quotes || 0) + (w.total_quotes || 0) + (s.total_quotes || 0);
                            const tI = (r.instructed   || 0) + (w.instructed   || 0) + (s.instructed   || 0);
                            return tQ ? Math.round((tI / tQ) * 100) : 0;
                          })()
                        : row.label === "Avg net / matter"
                          ? (() => {
                              const tI = (r.instructed || 0) + (w.instructed || 0) + (s.instructed || 0);
                              const tN = (r.net_legal_fees || 0) + (w.net_legal_fees || 0) + (s.net_legal_fees || 0);
                              return tI ? (tN / tI) : 0;
                            })()
                          : row.r + row.w + row.s;
                      return (
                        <tr key={idx} className="border-b border-slate-100 last:border-0" data-testid={`compare-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          <td className="px-2 py-2 font-medium text-slate-700">{row.label}</td>
                          <td className={`px-2 py-2 text-right tabular-nums ${row.accent || ""}`}>{row.fmt(row.r)}</td>
                          <td className={`px-2 py-2 text-right tabular-nums ${row.accent || ""}`}>{row.fmt(row.w)}</td>
                          <td className={`px-2 py-2 text-right tabular-nums ${row.accent || ""}`}>{row.fmt(row.s)}</td>
                          <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900">{row.fmt(total)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Acquisition channel comparison — superseded by the
            funnel-channel toggle above; users can compare channels by
            flipping between Referrer / Solicitor / Public on the
            toggle, which re-points every tile. */}

        {/* By transaction type — avg net per matter side by side so
            the firm can see which matter types are the most profitable
            on average. Same window/channel as the funnel above. */}
        {funnel.by_transaction_type && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm mb-8" data-testid="funnel-by-txtype">
            <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  By transaction type
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Average <strong>net</strong> legal fee per matter. {funnelTxType === "all"
                    ? "Same window as the funnel above."
                    : "Filter is currently scoped to one type — rows for other types will be zero."}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {[
                { key: "buying",     label: "Purchase",          accent: "emerald" },
                { key: "selling",    label: "Sale",              accent: "rose" },
                { key: "both",       label: "Sale + Purchase",   accent: "blue" },
                { key: "remortgage", label: "Remortgage",        accent: "amber" },
                { key: "transfer",   label: "Transfer of equity", accent: "purple" },
              ].map(({ key, label, accent }) => {
                const row = funnel.by_transaction_type[key] || { instructed: 0, net_legal_fees: 0, gross_legal_fees: 0, avg_net_per_matter: 0 };
                const accents = {
                  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
                  rose:    "border-rose-200 bg-rose-50 text-rose-900",
                  blue:    "border-blue-200 bg-blue-50 text-blue-900",
                  amber:   "border-amber-200 bg-amber-50 text-amber-900",
                  purple:  "border-purple-200 bg-purple-50 text-purple-900",
                }[accent];
                const empty = !row.instructed;
                return (
                  <div key={key}
                    className={`rounded-lg border p-4 ${empty ? "border-slate-200 bg-slate-50" : accents}`}
                    data-testid={`by-txtype-tile-${key}`}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
                    <p className="text-2xl font-bold mt-1 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {row.instructed ? formatGBP(row.avg_net_per_matter || 0) : "—"}
                    </p>
                    <p className="text-[10px] opacity-70 mt-0.5">
                      {row.instructed
                        ? `${row.instructed} instructed · ${formatGBP(row.net_legal_fees || 0)} net total`
                        : "No instructions in window."}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Top Referrers leaderboard — same window as the funnel. Surfaces who's
            actually delivering instructed cases (not just generating quotes), so
            the firm knows where to invest more relationship time. */}
        {Array.isArray(top_referrers) && top_referrers.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-sm mb-8" data-testid="top-referrers">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Referrers</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ranked by instructed cases · {funnelDays === 0 ? "All time" : `Last ${funnelDays} days`}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">
                    <th className="text-left py-2 pr-4">Referrer</th>
                    <th className="text-right py-2 px-2">Quotes</th>
                    <th className="text-right py-2 px-2 hidden sm:table-cell">PDFs</th>
                    <th className="text-right py-2 px-2">Instructed</th>
                    <th className="text-right py-2 px-2">Conv %</th>
                    <th className="text-right py-2 px-2" title="Net legal fees ex VAT from instructed quotes — includes the ex-VAT mark-up on ID checks / Search Pack / TT fees, minus referral commissions. Excludes SDLT and other pass-through disbursements.">Net Fees</th>
                    <th className="text-right py-2 pl-2" title="Output VAT collected on the legal fees + the VAT slice of the ID check / Search Pack / TT fee mark-up. Payable to HMRC, not firm revenue.">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {top_referrers.map((r, idx) => (
                    <tr
                      key={r.referrer_id || idx}
                      onClick={() => r.referrer_id && navigate(`/referrers?focus=${r.referrer_id}`)}
                      className={`border-b border-slate-100 last:border-0 transition-colors ${r.referrer_id ? "cursor-pointer hover:bg-slate-50" : ""}`}
                      data-testid={`referrer-leaderboard-row-${r.referrer_id}`}
                    >
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold shrink-0 ${
                            idx === 0 ? "bg-amber-100 text-amber-700" :
                            idx === 1 ? "bg-slate-200 text-slate-700" :
                            idx === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"
                          }`}>{idx + 1}</span>
                          <span className="font-medium text-slate-900 truncate">{r.name}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-2 text-slate-700">{r.total_quotes}</td>
                      <td className="text-right py-3 px-2 text-slate-500 hidden sm:table-cell">{r.pdfs_downloaded}</td>
                      <td className="text-right py-3 px-2 font-semibold text-slate-900">{r.instructed}</td>
                      <td className="text-right py-3 px-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                          r.conversion_pct >= 30 ? "bg-emerald-50 text-emerald-700" :
                          r.conversion_pct >= 10 ? "bg-amber-50 text-amber-700" :
                          "bg-slate-50 text-slate-500"
                        }`}>{r.conversion_pct.toFixed(1)}%</span>
                      </td>
                      <td className="text-right py-3 px-2 font-semibold text-slate-900 whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatGBP(r.instructed_value)}</td>
                      <td className="text-right py-3 pl-2 text-slate-600 whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatGBP(r.vat_collected || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Cases by Status */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="chart-cases-status">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Cases by Status</h3>
            {cases_by_status.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={cases_by_status} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {cases_by_status.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS_STATUS[index % COLORS_STATUS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No data yet</div>
            )}
          </div>

          {/* Cases by Type */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="chart-cases-type">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Cases by Type</h3>
            {cases_by_type.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={cases_by_type} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {cases_by_type.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS_TYPE[index % COLORS_TYPE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No data yet</div>
            )}
          </div>

          {/* Stage Distribution */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="chart-stage-distribution">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Active Cases by Current Stage</h3>
            {stage_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stage_distribution} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {stage_distribution.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS_STAGE[index % COLORS_STAGE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No active cases</div>
            )}
          </div>

          {/* Monthly Completions */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="chart-monthly-completions">
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Completions</h3>
            {monthly_completions.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthly_completions}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No completions yet</div>
            )}
          </div>
        </div>
      </main>

      {/* Funnel drill-down dialog — opens when a solicitor clicks a
          tile so they can see the actual quotes behind each number. */}
      <QuoteFunnelSegmentDialog
        open={!!openSegment}
        onOpenChange={(v) => !v && setOpenSegment(null)}
        segment={openSegment?.segment}
        label={openSegment?.label}
        funnelDays={funnelDays}
        channel={funnelChannel}
        transactionType={funnelTxType}
        propertyType={funnelPropType}
      />
    </div>
  );
}
